<template>
  <div class="templates-container">
    <base-nav
      title="Шаблоны документов"
      :show-back="true"
      :back-handler="goBack"
    >
      <base-primary-button icon="addSquare" @click="startCreating">
        Создать шаблон
      </base-primary-button>
    </base-nav>

    <base-loader v-if="isLoading && !templates.length" />

    <div v-else class="templates-list-wrapper">
      <ul class="list">
        <!-- Форма создания нового шаблона -->
        <li v-if="isCreating" class="card edit-form-container">
          <TemplateEditForm
            :company-id="companyId"
            @save="handleCreateSuccess"
            @cancel="cancelCreating"
          />
        </li>

        <li
          v-for="template in localTemplates"
          :key="template.id"
          class="template-list-item"
          @click="goToTemplateDetails(template.id)"
        >
          <TemplateMainInfo
            :template="template"
            :company-id="companyId"
            @toggle-status="handleToggleStatus"
            @duplicate="handleDuplicate"
            @render="
              (showPreviewModal = true),
                (currentTemplate = template.id),
                (currentKind = template.kind)
            "
          />
        </li>
      </ul>

      <div
        v-if="!isLoading && templates.length === 0 && !isCreating"
        class="no-data"
      >
        Список шаблонов пуст
      </div>
    </div>

    <!-- Модальное окно -->
    <TemplatePreviewModal
      :show="showPreviewModal"
      :is-loading="isPreviewLoading"
      :kind="currentKind"
      @close="showPreviewModal = false"
      @submit="handlePreviewSubmit"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";

import { useTemplateStore } from "@/stores/templateStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { getCompanyId } from "@/utils/userUtils";

import TemplateMainInfo from "@/components/Template/TemplateMainInfo.vue";
import TemplateEditForm from "@/components/Template/TemplateEditForm.vue";
import TemplatePreviewModal from "@/components/Template/TemplatePreviewModal.vue";

export default {
  name: "TemplatesPage",
  components: {
    TemplateEditForm,
    TemplateMainInfo,
    TemplatePreviewModal,
  },
  data() {
    return {
      companyId: getCompanyId(),

      isCreating: false,

      showPreviewModal: false,
      isPreviewLoading: false,
      currentTemplate: null,
      currentKind: null,
      localTemplates: [],
    };
  },

  computed: {
    ...mapState(useTemplateStore, ["templates", "isLoading"]),

    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadTemplates(newPage);
      },
    },
  },
  methods: {
    ...mapActions(useTemplateStore, [
      "getTemplatesRequest",
      "templateEnable",
      "templateDisable",
      "templateDuplicate",
      "renderTemplate",
    ]),

    async loadTemplates() {
      try {
        await this.getTemplatesRequest();
        this.localTemplates = [...this.templates];
      } catch (error) {
        console.error("Ошибка загрузки шаблонов", error);
      }
    },

    updateLocalTemplate(updatedTemplate) {
      const target = this.localTemplates.find(
        (t) => t.id === updatedTemplate.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedTemplate);
      }
    },

    goToTemplateDetails(id) {
      this.$router.push({
        name: "TemplateDetails",
        params: { id },
      });
    },

    goBack() {
      const previous = this.$router.options.history.state?.back;
      if (previous && this.$route.query.targetId) {
        this.$router.push({
          name: "OrderDetails",
          params: { id: this.$route.query.targetId },
          query: { tab: "documents" },
        });
      } else {
        this.$router.back();
      }
    },

    startCreating() {
      this.isCreating = true;
      this.editingId = null;
    },

    cancelCreating() {
      this.isCreating = false;
    },

    handleCreateSuccess() {
      this.isCreating = false;

      this.notificationStore.showSuccess("Шаблон успешно создан");
      this.loadTemplates();
    },

    async handleToggleStatus(template) {
      try {
        if (template.enabled) {
          await this.templateDisable(template.id);
          this.updateLocalTemplate({ ...template, enabled: false });
          this.notificationStore.showSuccess(
            "Шаблон успешно отключен"
          );
        } else {
          await this.templateEnable(template.id);
          this.updateLocalTemplate({ ...template, enabled: true });
          this.notificationStore.showSuccess(
            "Шаблон успешно включен"
          );
        }
      } catch (error) {
        console.error("Ошибка смены статуса", error);
      }
    },

    async handleDuplicate(template) {
      try {
        await this.templateDuplicate(template.id);
        await this.loadTemplates();
        this.notificationStore.showSuccess(
          "Шаблон успешно дублирован"
        );
      } catch (error) {
        console.error("Ошибка дублирования шаблона", error);
      }
    },

    async handlePreviewSubmit(params) {
      this.isPreviewLoading = true;
      try {
        await this.renderTemplate(this.currentTemplate, params);

        this.showPreviewModal = false;
      } catch (error) {
        console.error("Ошибка при предпросмотре документа", error);
      } finally {
        this.isPreviewLoading = false;
      }
    },
  },
  async created() {
    await this.loadTemplates();
  },
};
</script>

<style scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  list-style: none;
}

.edit-form-container {
  padding: var(--base-in-indent);
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
}
</style>
